import {
  MutationCache,
  QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { meQueryKey } from '@/api/queryKeys';

import { toaster } from './toaster';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: CustomMutationMeta;
  }
}

interface CustomMutationMeta {
  skipGlobalErrorHandler?: boolean;
}

function extractErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (typeof data === 'object' && data !== null) {
      if ('error' in data && typeof data.error === 'string') return data.error;
      if (
        'errors' in data &&
        typeof data.errors === 'object' &&
        data.errors !== null
      ) {
        return Object.values(data.errors).join(', ');
      }
      if ('message' in data && typeof data.message === 'string')
        return data.message;
    }

    // A 403 carries no body worth showing: the authorization middleware answers
    // text/plain "forbidden". Say what it actually means — the caller's own scope
    // may have changed under them (#419 AC 13).
    if (error.response?.status === 403)
      return 'You are not allowed to do this — your permissions may have changed. Reload to see your current scope.';

    if (typeof data === 'string') return data;

    return error.message;
  }

  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;

  return 'An unexpected error occurred';
}

export type MutationConfig<
  MutationFnType extends (...args: any[]) => Promise<unknown>,
> = UseMutationOptions<
  Awaited<ReturnType<MutationFnType>>,
  Error,
  Parameters<MutationFnType>[0]
>;

export const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // The gates read a cached /v1/users/me. A 403 says the cache and the
      // server disagree, so drop it rather than keep gating on it (#419 AC 13).
      // Ahead of the opt-out: suppressing a toast must not also suppress fixing
      // the cache.
      if (isAxiosError(error) && error.response?.status === 403) {
        void queryClient.invalidateQueries({
          queryKey: meQueryKey.all,
        });
      }

      if (mutation.meta?.skipGlobalErrorHandler) {
        return;
      }
      toaster.error(extractErrorMessage(error));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 2 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
    },
  },
});
